import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Plus, Edit2, Trash2, Download, Printer } from 'lucide-react';
import { DataStore, Quote } from '@/lib/data-store';
import { QuotationForm } from '@/components/QuotationForm';
import { ProjectBreadcrumb } from '@/components/ProjectBreadcrumb';
import { useProject } from '@/context/ProjectContext';
import { exportToExcel, printTable } from '@/lib/export-utils';
import { printWithLetterhead, generateTableHTML } from '@/lib/print-utils';
import { generateQuotationNumber, getNextQuotationNumber } from '@/lib/quotation-utils';

const Sebutharga = () => {
  const data = DataStore.getData();
  const [quotes, setQuotes] = useState<Quote[]>(data.quotes);
  const [customers] = useState(data.customers);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { selectedProject, setSelectedProject } = useProject();

  const handleAddNew = () => {
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
    setShowForm(true);
  };

  const handleSubmit = (data: Quote) => {
    const newQuotes = editingId
      ? quotes.map(q => q.id === editingId ? { ...data, id: editingId } : q)
      : [...quotes, data];

    setQuotes(newQuotes);
    DataStore.saveData({ ...DataStore.getData(), quotes: newQuotes });
    setShowForm(false);
  };

  const handleDelete = (id: string) => {
    const filtered = quotes.filter(q => q.id !== id);
    setQuotes(filtered);
    DataStore.saveData({ ...DataStore.getData(), quotes: filtered });
  };

  const filteredQuotes = useMemo(() => quotes.filter(q => {
    const matchesSearch = q.no.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          q.pelanggan.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProject = !selectedProject || q.projek === selectedProject;
    return matchesSearch && matchesProject;
  }), [quotes, searchTerm, selectedProject]);

  const nextQuoteNo = generateQuotationNumber(getNextQuotationNumber(quotes));

  return (
    <div className="space-y-6">
      {selectedProject && (
        <ProjectBreadcrumb projectName={selectedProject} currentPage="quotation" />
      )}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Sebutharga</h1>
          <p className="text-slate-600 mt-1">Urus penawaran harga untuk pelanggan</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {quotes.length > 0 && (
            <>
              <Button 
                onClick={() => {
                  const tableHTML = generateTableHTML(filteredQuotes, [
                    { label: 'No. Sebutharga', key: 'no' },
                    { label: 'Pelanggan', key: 'pelanggan' },
                    { label: 'Nama Projek', key: 'namaProyek' },
                    { label: 'Tarikh', key: 'tanggal' },
                    { label: 'Total (RM)', key: 'jumlah', format: (v) => v ? `RM ${v.toFixed(2)}` : '-' },
                    { label: 'Status', key: 'status' }
                  ]);
                  printWithLetterhead('DAFTAR SEBUTHARGA', tableHTML, 'SEBUTHARGA / QUOTATION');
                }} 
                className="bg-green-600 hover:bg-green-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Printer size={18} />
                <span className="font-semibold">Cetak</span>
              </Button>
              <Button 
                onClick={() => exportToExcel(filteredQuotes, 'Sebutharga')} 
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Download size={18} />
                <span className="font-semibold">Excel</span>
              </Button>
            </>
          )}
          <Button className="bg-red-600 hover:bg-red-700 gap-2 px-4 py-2 h-auto" onClick={handleAddNew}>
            <Plus size={18} />
            <span className="font-semibold">Sebutharga Baru</span>
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-slate-400" size={20} />
            <Input 
              placeholder="Cari sebutharga..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-slate-300"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Daftar Sebutharga</CardTitle>
          <CardDescription>Jumlah {filteredQuotes.length} penawaran</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredQuotes.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Tiada data sebutharga. Sila tambahkan penawaran baru.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table id="quotes-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">No. Sebutharga</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Pelanggan</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Tarikh</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Jumlah</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuotes.map((quote: any) => (
                    <tr key={quote.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium text-slate-900">{quote.no}</td>
                      <td className="py-3 px-4 text-slate-600 font-medium text-blue-600 hover:underline cursor-pointer" onClick={() => setSelectedProject(quote.namaProyek || quote.pelanggan)}>{quote.pelanggan}</td>
                      <td className="py-3 px-4 text-slate-600">{quote.tanggal}</td>
                      <td className="py-3 px-4 font-medium text-slate-900 text-right">RM {(quote.jumlah || quote.total || 0).toFixed(2)}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                          quote.status === 'Diterima' ? 'bg-green-100 text-green-700' :
                          quote.status === 'Draft' ? 'bg-blue-100 text-blue-700' :
                          quote.status === 'Menunggu' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {quote.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 flex gap-2">
                        <button onClick={() => handleEdit(quote.id)} className="text-blue-600 hover:text-blue-700 text-sm gap-1 flex items-center">
                          <Edit2 size={14} /> Sunting
                        </button>
                        <button onClick={() => handleDelete(quote.id)} className="text-red-600 hover:text-red-700 text-sm gap-1 flex items-center">
                          <Trash2 size={14} /> Padam
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {showForm && (
        <QuotationForm
          quote={editingId ? quotes.find(q => q.id === editingId) : undefined}
          noSebutharga={editingId ? (quotes.find(q => q.id === editingId)?.no || nextQuoteNo) : nextQuoteNo}
          customers={customers}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
};

export default Sebutharga;